import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Plus, FileText, Pencil, Trash2 } from "lucide-react";
import { api, downloadBlob } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, fmtDate, todayISO, addDaysISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyTime = (d = {}) => ({ date: todayISO(), hours: "", rate: d.rate ?? "", person: d.person ?? "", description: "" });

export default function JobDetail() {
  const { id } = useParams();
  const { currentId, current } = useCompany();
  const navigate = useNavigate();
  const [basis, setBasis] = useState("accrual");
  const [start, setStart] = useState("2015-01-01");
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTime());
  const [editId, setEditId] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ include_time: true, include_expenses: false, expense_markup: "", issue_date: todayISO(), due_date: addDaysISO(30) });
  const [generating, setGenerating] = useState(false);

  const load = useCallback(() => {
    if (!currentId) return;
    api.get(`/jobs/${id}/detail?start=${start}&end=${end}&basis=${basis}`).then((r) => setData(r.data)).catch(() => navigate("/jobs"));
  }, [currentId, id, start, end, basis, navigate]);

  useEffect(() => { load(); }, [load]);

  const defaults = () => ({
    person: current?.default_timekeeper || "",
    rate: current?.default_rate ? String(current.default_rate) : "",
  });
  const clientRates = data?.client_rates || [];
  const hasClient = !!data?.job?.client_id;
  const openNewTime = () => {
    if (!hasClient) return toast.error("Add a client to this job before logging time.");
    setForm(emptyTime(defaults()));
    setEditId(null);
    setOpen(true);
  };
  const pickPerson = (name) => {
    const match = clientRates.find((r) => r.name === name);
    setForm((f) => ({ ...f, person: name, rate: match && match.rate != null ? String(match.rate) : f.rate }));
  };
  const openEditTime = (t2) => {
    setForm({ date: t2.date, hours: String(t2.hours), rate: String(t2.rate), person: t2.person || "", description: t2.description || "" });
    setEditId(t2.id);
    setOpen(true);
  };

  const saveTime = async () => {
    const hours = parseFloat(form.hours);
    if (!hours || hours <= 0) return toast.error("Enter valid hours");
    if (!form.person) return toast.error("Select a person");
    const payload = { job_id: id, date: form.date, hours, rate: parseFloat(form.rate) || 0, person: form.person, description: form.description };
    try {
      if (editId) await api.put(`/time/${editId}`, payload);
      else await api.post("/time", payload);
      toast.success(editId ? "Time updated" : "Time logged");
      setOpen(false);
      setForm(emptyTime(defaults()));
      setEditId(null);
      load();
    } catch (e) {
      toast.error("Could not save time");
    }
  };

  const removeTime = async (tid) => {
    if (!window.confirm("Delete this time entry?")) return;
    try {
      await api.delete(`/time/${tid}`);
      toast.success("Time entry deleted");
      load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const exportJob = async () => {
    try {
      await downloadBlob(`/reports/export/pdf?start=${start}&end=${end}&basis=${basis}&job_id=${id}`, `job-pack-${start}-to-${end}.pdf`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const generate = async () => {
    if (!gen.include_time && !gen.include_expenses) return toast.error("Choose time and/or expenses to bill");
    setGenerating(true);
    try {
      const res = await api.post(`/jobs/${id}/generate-invoice`, {
        include_time: gen.include_time,
        include_expenses: gen.include_expenses,
        expense_markup: parseFloat(gen.expense_markup) || 0,
        issue_date: gen.issue_date,
        due_date: gen.due_date,
      });
      toast.success(`Draft ${res.data.invoice_number} created`);
      setGenOpen(false);
      navigate(`/invoices/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate invoice");
    } finally {
      setGenerating(false);
    }
  };

  const job = data?.job;
  const t = data?.totals || {};

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate("/jobs")} data-testid="back-to-jobs" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to jobs
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            {job?.code && <span className="text-slate-400 tabular-nums mr-2">{job.code}</span>}{job?.name}
          </h1>
          <div className="text-slate-500 text-sm mt-1 flex items-center gap-2">
            <span>{job?.client_name || "No client"}</span>
            <span>·</span>
            <Badge variant="outline" className={`capitalize ${job?.status === "closed" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{job?.status}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="job-generate-invoice-btn" onClick={() => setGenOpen(true)} className="rounded-md bg-slate-900 hover:bg-slate-800">
            <FileText className="h-4 w-4 mr-2" /> Generate Invoice
          </Button>
          <Button data-testid="job-export-btn" onClick={exportJob} variant="outline" className="rounded-md">
            <FileDown className="h-4 w-4 mr-2" /> Export Job Pack (PDF)
          </Button>
        </div>
      </div>

      <Card className="p-4 rounded-lg border-slate-200 shadow-sm mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label>From</Label>
            <div className="mt-1"><DatePicker testId="jobdetail-start" value={start} onChange={setStart} /></div>
          </div>
          <div>
            <Label>To</Label>
            <div className="mt-1"><DatePicker testId="jobdetail-end" value={end} onChange={setEnd} /></div>
          </div>
          <div>
            <Label>Basis</Label>
            <div className="mt-1 inline-flex rounded-md border border-slate-200 overflow-hidden">
              {["accrual", "cash"].map((b) => (
                <button key={b} data-testid={`jobdetail-basis-${b}`} onClick={() => setBasis(b)}
                  className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${basis === b ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Summary label="Income" value={money(t.income)} tone="text-emerald-700" testid="jobdetail-income" />
        <Summary label="Expenses" value={money(t.expense)} tone="text-amber-700" testid="jobdetail-expense" />
        <Summary label="Labour" value={money(t.labour)} tone="text-amber-700" testid="jobdetail-labour" />
        <Summary label="Profit" value={money(t.profit)} tone={t.profit >= 0 ? "text-slate-900" : "text-rose-600"} testid="jobdetail-profit" primary />
      </div>

      <DetailTable title="Invoices" cols={["Invoice", "Date", "Status", "Total", "Counted"]} rows={data?.invoices} empty="No invoices coded to this job."
        render={(i) => (
          <tr key={i.id} className="border-b border-slate-50">
            <td className="px-5 py-2 font-medium text-slate-900">{i.invoice_number}<span className="block text-xs text-slate-400">{i.client_name}</span></td>
            <td className="px-5 py-2 text-slate-600">{fmtDate(i.date)}</td>
            <td className="px-5 py-2 capitalize text-slate-600">{i.status}</td>
            <td className="px-5 py-2 text-right tabular-nums">{money(i.total)}</td>
            <td className="px-5 py-2 text-right tabular-nums text-emerald-700">{money(i.recognised)}</td>
          </tr>
        )} />

      {data?.recurring?.length > 0 && (
        <DetailTable title="Recurring Income" cols={["Source", "Date", "Amount", "Counted"]} rows={data.recurring} empty=""
          render={(r) => (
            <tr key={r.id} className="border-b border-slate-50">
              <td className="px-5 py-2 font-medium text-slate-900">{r.source}</td>
              <td className="px-5 py-2 text-slate-600">{fmtDate(r.date)}</td>
              <td className="px-5 py-2 text-right tabular-nums">{money(r.amount)}</td>
              <td className="px-5 py-2 text-right tabular-nums text-emerald-700">{money(r.recognised)}</td>
            </tr>
          )} />
      )}

      <DetailTable title="Expenses" cols={["Vendor", "Date", "Account", "Amount", "Counted"]} rows={data?.expenses} empty="No expenses coded to this job."
        render={(e) => (
          <tr key={e.id} className="border-b border-slate-50">
            <td className="px-5 py-2 font-medium text-slate-900">{e.vendor}{e.job_charge && <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">Job charge</Badge>}</td>
            <td className="px-5 py-2 text-slate-600">{fmtDate(e.date)}</td>
            <td className="px-5 py-2 text-slate-600">{e.account_name}</td>
            <td className="px-5 py-2 text-right tabular-nums">{money(e.amount)}</td>
            <td className="px-5 py-2 text-right tabular-nums text-amber-700">{money(e.counted)}</td>
          </tr>
        )} />

      <div className="flex items-center justify-between mt-8 mb-2">
        <h2 className="font-heading font-semibold text-slate-900">Time / Labour</h2>
        <Button size="sm" data-testid="jobdetail-add-time-btn" onClick={openNewTime} disabled={!hasClient} title={!hasClient ? "Add a client to this job first" : ""} className="rounded-md bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-1" /> Log Time
        </Button>
      </div>
      {!hasClient && (
        <p className="text-xs text-amber-600 mb-2" data-testid="jobdetail-no-client-note">Attach a client to this job (edit it under Jobs) to log time — rates come from the client's people.</p>
      )}
      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0" data-testid="jobdetail-time-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
              <th className="px-5 py-2 font-medium">Date</th>
              <th className="px-5 py-2 font-medium">Person</th>
              <th className="px-5 py-2 font-medium">Notes</th>
              <th className="px-5 py-2 font-medium text-right">Hours</th>
              <th className="px-5 py-2 font-medium text-right">Rate</th>
              <th className="px-5 py-2 font-medium text-right">Bill</th>
              <th className="px-5 py-2 font-medium text-right">Cost</th>
              <th className="px-5 py-2 font-medium text-right"></th>
            </tr>
          </thead>
          <tbody>
            {(data?.time || []).map((t2) => (
              <tr key={t2.id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-5 py-2 text-slate-600">{fmtDate(t2.date)}</td>
                <td className="px-5 py-2 text-slate-600">{t2.person || "—"}</td>
                <td className="px-5 py-2 text-slate-600">{t2.description || "—"}</td>
                <td className="px-5 py-2 text-right tabular-nums">{(t2.hours || 0).toFixed(2)}</td>
                <td className="px-5 py-2 text-right tabular-nums text-slate-600">{money(t2.rate)}</td>
                <td className="px-5 py-2 text-right tabular-nums text-emerald-700">{money(t2.billable)}</td>
                <td className="px-5 py-2 text-right tabular-nums text-amber-700">{money(t2.cost)}</td>
                <td className="px-5 py-2 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" data-testid={`jobdetail-edit-time-${t2.id}`} onClick={() => openEditTime(t2)}>
                    <Pencil className="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`jobdetail-delete-time-${t2.id}`} onClick={() => removeTime(t2.id)}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </td>
              </tr>
            ))}
            {(!data?.time || data.time.length === 0) && (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">No time logged to this job.</td></tr>
            )}
          </tbody>
          {data?.time?.length > 0 && (
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold text-slate-900">
                <td className="px-5 py-2" colSpan={3}>Total</td>
                <td className="px-5 py-2 text-right tabular-nums" data-testid="jobdetail-total-hours">
                  {data.time.reduce((s, r) => s + (r.hours || 0), 0).toFixed(2)}
                </td>
                <td className="px-5 py-2"></td>
                <td className="px-5 py-2 text-right tabular-nums text-emerald-700" data-testid="jobdetail-total-billable">
                  {money(data.time.reduce((s, r) => s + (r.billable || 0), 0))}
                </td>
                <td className="px-5 py-2 text-right tabular-nums text-amber-700" data-testid="jobdetail-total-labour-cost">
                  {money(data.time.reduce((s, r) => s + (r.cost || 0), 0))}
                </td>
                <td className="px-5 py-2"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Time" : "Log Time to this Job"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <div className="mt-1"><DatePicker testId="jobdetail-time-date" value={form.date} onChange={(d) => setForm({ ...form, date: d })} /></div>
              </div>
              <div>
                <Label>Person</Label>
                <Select value={form.person || undefined} onValueChange={pickPerson}>
                  <SelectTrigger data-testid="jobdetail-time-person" className="mt-1 rounded-md">
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    {clientRates.map((r) => (
                      <SelectItem key={r.name} value={r.name}>{r.name}{r.rate ? ` · $${r.rate}/hr` : ""}</SelectItem>
                    ))}
                    {form.person && !clientRates.some((r) => r.name === form.person) && (
                      <SelectItem value={form.person}>{form.person}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {clientRates.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No people on this client yet — add them on the Clients page.</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hours</Label>
                <Input type="number" step="0.25" data-testid="jobdetail-time-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" />
              </div>
              <div>
                <Label>Rate ($/hr)</Label>
                <Input type="number" step="0.01" data-testid="jobdetail-time-rate" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input data-testid="jobdetail-time-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-md" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="jobdetail-save-time" onClick={saveTime} className="rounded-md bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Generate Invoice from Job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Pulls <span className="font-medium text-slate-700">unbilled</span> items from this job into a draft invoice for {job?.client_name || "the job's client"}. GST (5%) is added automatically.
            </p>
            <label className="flex items-start gap-3 rounded-md border border-slate-200 p-3 cursor-pointer">
              <Checkbox data-testid="gen-include-time" checked={gen.include_time} onCheckedChange={(v) => setGen({ ...gen, include_time: !!v })} className="mt-0.5" />
              <span className="text-sm">
                <span className="font-medium text-slate-900">Time / labour</span>
                <span className="block text-slate-400">One line per person · {data?.unbilled?.time_count || 0} unbilled entries · {money(data?.unbilled?.time)}</span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-md border border-slate-200 p-3 cursor-pointer">
              <Checkbox data-testid="gen-include-expenses" checked={gen.include_expenses} onCheckedChange={(v) => setGen({ ...gen, include_expenses: !!v })} className="mt-0.5" />
              <span className="text-sm">
                <span className="font-medium text-slate-900">Expenses</span>
                <span className="block text-slate-400">{data?.unbilled?.expense_count || 0} unbilled · {money(data?.unbilled?.expenses)} at cost</span>
              </span>
            </label>
            {gen.include_expenses && (
              <div>
                <Label>Expense markup %</Label>
                <Input type="number" step="1" data-testid="gen-markup-input" value={gen.expense_markup} onChange={(e) => setGen({ ...gen, expense_markup: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" placeholder="0" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue date</Label>
                <div className="mt-1"><DatePicker testId="gen-issue-date" value={gen.issue_date} onChange={(d) => setGen({ ...gen, issue_date: d })} /></div>
              </div>
              <div>
                <Label>Due date</Label>
                <div className="mt-1"><DatePicker testId="gen-due-date" value={gen.due_date} onChange={(d) => setGen({ ...gen, due_date: d })} /></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button data-testid="gen-create-btn" onClick={generate} disabled={generating} className="rounded-md bg-slate-900 hover:bg-slate-800">
              {generating ? "Creating…" : "Create Draft Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Summary({ label, value, tone, testid, primary }) {
  return (
    <Card className={`p-4 rounded-lg border shadow-sm ${primary ? "bg-slate-900 border-slate-900" : "bg-white border-slate-200"}`}>
      <div className={`text-xs uppercase tracking-wide ${primary ? "text-slate-300" : "text-slate-400"}`}>{label}</div>
      <div className={`font-heading text-2xl font-bold tabular-nums mt-1 ${primary ? "text-white" : tone}`} data-testid={testid}>{value}</div>
    </Card>
  );
}

function DetailTable({ title, cols, rows, render, empty }) {
  return (
    <div className="mb-6">
      <h2 className="font-heading font-semibold text-slate-900 mb-2">{title}</h2>
      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
              {cols.map((c, i) => <th key={c} className={`px-5 py-2 font-medium ${i >= 3 ? "text-right" : ""}`}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map(render)}
            {(!rows || rows.length === 0) && (
              <tr><td colSpan={cols.length} className="px-5 py-8 text-center text-slate-400">{empty || "None."}</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
